import React, { Fragment } from 'react';

import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

const REASON_MAX_LENGTH = 1000;

// Mirrors ArchiveActualCostRequest's `nullable|string|max:1000`. Optional on
// purpose: whoever archives a transaction already knows why, and demanding a
// reason only adds friction to an action that can be undone.
const ArchiveTransactionSchema = Yup.object().shape({
    reason: Yup.string().max(REASON_MAX_LENGTH, $t('The reason must not be longer than {{max}} characters.', { max: REASON_MAX_LENGTH })),
});

/**
 * Confirmation prompt for archiving a single reconciliation transaction.
 *
 * A modal of its own rather than the shared ConfirmationModalContext, which
 * accepts only title / message / warning / actions and cannot host the reason
 * field.
 */
export default function ArchiveTransactionModal({ modal, row, onConfirm, archiving }) {
    const close = () => modal.hide();

    return (
        <Modal id="archive-actual-cost-modal" modal={modal}>
            <ModalHeader close={close}>{$t('Archive transaction')}</ModalHeader>
            {/* Keyed on the row: initialValues is deep-equal between renders, so
                enableReinitialize alone would carry one row's typed reason over to
                the next row the prompt opens on. */}
            <Formik
                key={row?.id ?? 'none'}
                enableReinitialize
                initialValues={{ reason: '' }}
                validationSchema={ArchiveTransactionSchema}
                onSubmit={(values, actions) => {
                    Promise.resolve(onConfirm(values.reason.trim())).finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit }) => (
                    <Fragment>
                        <div className="tw-mb-6 tw-space-y-4">
                            <p className="tw-mb-0 tw-text-sm tw-text-charcoal">
                                {$t('This transaction will be excluded from the actuals vs budget report and every other total. You can restore it later.')}
                            </p>

                            {!!row && (
                                <div className="tw-rounded-lg tw-bg-charcoal-10 tw-px-4 tw-py-3 tw-text-sm">
                                    <div className="tw-font-semibold tw-text-charcoal">{row.transaction_description || $t('Untitled transaction')}</div>
                                    <div className="tw-text-charcoal-75">
                                        {[row.supplier || row.supplier_name, formatToFixedMoneyWithLocale(row.amount)].filter(Boolean).join(' — ')}
                                    </div>
                                </div>
                            )}

                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <label htmlFor="archive-reason" className="text-input__label">
                                    {$t('Reason')}{' '}
                                    <span className="tw-rounded tw-bg-charcoal-15 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-charcoal-75">
                                        {$t('Optional')}
                                    </span>
                                </label>
                                <Field
                                    as="textarea"
                                    id="archive-reason"
                                    name="reason"
                                    rows={3}
                                    maxLength={REASON_MAX_LENGTH}
                                    disabled={isSubmitting || archiving}
                                    className="form-control !tw-bg-grey/50 tw-rounded-lg tw-border-0"
                                    placeholder={$t('e.g. Duplicate of INV-4470, supplier re-issued')}
                                />
                                {touched.reason && errors.reason && <div className="tw-text-sm tw-text-red">{errors.reason}</div>}
                                <div className="tw-text-xs tw-text-charcoal-50">
                                    {$t('{{count}} of {{max}} characters', { count: values.reason.length, max: REASON_MAX_LENGTH })}
                                </div>
                            </div>
                        </div>
                        <ModalFooter
                            submitButtonVariant="primary"
                            cancelButtonText={$t('Cancel')}
                            primaryButtonText={archiving ? $t('Archiving…') : $t('Archive')}
                            primaryButtonDisabled={archiving}
                            isSubmitting={isSubmitting}
                            onCancel={close}
                            handleSubmit={() => handleSubmit()}
                        />
                    </Fragment>
                )}
            </Formik>
        </Modal>
    );
}
